import React, { useEffect, useMemo, useState, useContext } from "react";
import { Clock } from "lucide-react";

import SectionCard from "../Orders Main/ui/SectionCard";
import OrdersFilters from "../Orders Main/ui/OrdersFilters";
import OrdersTable from "../Orders Main/ui/OrdersTable";
import OrderDetailModal from "../Orders Main/ui/OrderDetailModal";
import Pagination from "../Orders Main/ui/Pagination";
import DeliveryServiceType from "../Orders Main/ui/DeliveryServiceType";
import OrderPaidDetail from "../Orders Main/ui/OrderPaidDetail";
import OrderEventsModal from "../Orders Main/ui/OrderEventsModal";

import { toInputDate } from "../Orders Main/utils/date";
import { useOrdersData } from "../Orders Main/hooks/useOrdersData";
import useOrderActions from "../Orders Main/hooks/useOrderActions";
import { GlobalContext } from "../../Context/GlobalContext";
import useOrders from "../Services/OrderServices/useOrders";

const PAGE_SIZE = 20;

export default function ReconAllOrders() {
  const {
    AdminPk,
    clientOptions,
    newOrders,
    historyOrders,
    loadingNew,
    loadingHis,
    getNewOrder,
    getHistory,

    getOrderEvents,
  } = useOrdersData();
  const { getOrderDetails } = useOrders();

  const { ordersUpdateTick, wsConnected } = useContext(GlobalContext);

  const [activeTab, setActiveTab] = useState("current");
  const [client, setClient] = useState("");
  const [search, setSearch] = useState("");

  const today = useMemo(() => new Date(), []);
  const [currentFrom, setCurrentFrom] = useState(toInputDate(today));
  const [currentTo, setCurrentTo] = useState(toInputDate(today));

  const dEnd = new Date();
  const dStart = new Date();
  dStart.setDate(dEnd.getDate() - 7);

  const [historyFrom, setHistoryFrom] = useState(toInputDate(dStart));
  const [historyTo, setHistoryTo] = useState(toInputDate(dEnd));

  const [pageCurrent, setPageCurrent] = useState(1);
  const [pageHistory, setPageHistory] = useState(1);

  // Order detail modal
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  const [paymentOrder, setPaymentOrder] = useState(null);
  const [paidOrder, setPaidOrder] = useState(null);
  const [eventsOpen, setEventsOpen] = useState(false);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsOrderId, setEventsOrderId] = useState(null);
  const [orderEvents, setOrderEvents] = useState([]);

  const { accept, prepared, dispatched, delivered, reject, mark_paid } =
    useOrderActions({
      refresh: async () => {
        if (activeTab === "current") {
          await getNewOrder({ client, from: currentFrom, to: currentTo });
        } else {
          await getHistory({ client, from: historyFrom, to: historyTo });
        }
      },
    });

  // Client-side search
  const applySearch = (rows) => {
    const q = (search || "").trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((r) => {
      return (
        String(r.orderid || "")
          .toLowerCase()
          .includes(q) ||
        String(r.customer_name || "")
          .toLowerCase()
          .includes(q) ||
        String(r.customer_mobile || "")
          .toLowerCase()
          .includes(q) ||
        String(r.organization_name || "")
          .toLowerCase()
          .includes(q)
      );
    });
  };

  const rowsCurrent = applySearch(newOrders || []);
  const rowsHistory = applySearch(historyOrders || []);

  const totalCurrent = rowsCurrent.length;
  const totalHistory = rowsHistory.length;

  // Initial + when admin / tab / client changes
  useEffect(() => {
    if (!AdminPk) return;
    if (activeTab === "current") {
      getNewOrder({ client, from: currentFrom, to: currentTo });
    } else {
      getHistory({ client, from: historyFrom, to: historyTo });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [AdminPk, activeTab, client]);

  // Re-fetch on date change – current tab
  useEffect(() => {
    if (!AdminPk || activeTab !== "current") return;
    setPageCurrent(1);
    getNewOrder({ client, from: currentFrom, to: currentTo });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFrom, currentTo]);

  // Re-fetch on date change – history tab
  useEffect(() => {
    if (!AdminPk || activeTab !== "history") return;
    setPageHistory(1);
    getHistory({ client, from: historyFrom, to: historyTo });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyFrom, historyTo]);

  // WebSocket pe active tab refresh
  useEffect(() => {
    if (!AdminPk) return;
    if (!ordersUpdateTick) return;
    if (activeTab === "current") {
      getNewOrder({ client, from: currentFrom, to: currentTo });
    } else {
      getHistory({ client, from: historyFrom, to: historyTo });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordersUpdateTick]);

  const refreshActive = () => {
    if (activeTab === "current") {
      getNewOrder({ client, from: currentFrom, to: currentTo });
    } else {
      getHistory({ client, from: historyFrom, to: historyTo });
    }
  };

  const onCloseDetails = () => {
    setOpen(false);
    setSelected(null);
    setDetail(null);
  };

  const onAction = async (key, ord) => {
    const map = { accept, prepared, dispatched, delivered, reject, mark_paid };
    if (map[key]) await map[key](ord);
  };

  const onView = async (row) => {
    setSelected(row);
    setOpen(true);
    setDetail(null);
    setDetailLoading(true);
    try {
      const d = await getOrderDetails(row?.orderid);
      setDetail(d);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleClosePaymentModal = () => {
    setPaymentOrder(null);
  };

  const handlePaymentSuccess = async () => {
    setPaymentOrder(null);
    refreshActive();
  };

  const handleStatusAction = (statusKey, ord) => {
    if (activeTab === "history") {
      return;
    }

    const orderType = (ord?.orderType || "").toLowerCase();

    if (orderType === "home_delivery") {
      if (statusKey === "paid") {
        setPaidOrder(ord);
        setOpen(false);
      }
      return;
    }

    if (statusKey === "unpaid") {
      setPaymentOrder(ord);
    } else if (statusKey === "paid") {
      setPaidOrder(ord);
      setOpen(false);
    }
  };
  const onDelayClick = async (row) => {
    const oid = row?.orderid;
    if (!oid) return;

    setEventsOpen(true);
    setEventsOrderId(oid);
    setOrderEvents([]);
    setEventsLoading(true);

    try {
      const ev = await getOrderEvents(oid);
      setOrderEvents(ev || []);
    } finally {
      setEventsLoading(false);
    }
  };

  return (
    <div className="space-y-5 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-2xl bg-emerald-50 text-emerald-600">
            <Clock size={18} />
          </div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold">All Orders</h1>

            <span
              className={`inline-flex items-center gap-1 rounded-full px-2 py-1 mt-1 ${
                wsConnected
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-red-50 text-red-600"
              }`}
            >
              <span
                className={`h-2 w-2 rounded-full ${
                  wsConnected ? "bg-emerald-500" : "bg-red-500"
                }`}
              ></span>
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center rounded-xl bg-gray-100 p-1">
          <button
            onClick={() => setActiveTab("current")}
            className={`h-8 rounded-lg px-3 text-xs font-semibold transition ${
              activeTab === "current"
                ? "bg-white shadow-sm"
                : "text-gray-600 hover:text-gray-800"
            }`}
          >
            Current Orders
          </button>
          <button
            onClick={() => setActiveTab("history")}
            className={`h-8 rounded-lg px-3 text-xs font-semibold transition ${
              activeTab === "history"
                ? "bg-white shadow-sm"
                : "text-gray-600 hover:text-gray-800"
            }`}
          >
            Order History
          </button>
        </div>
      </div>

      <OrdersFilters
        activeTab={activeTab}
        client={client}
        setClient={(v) => {
          setClient(v);
          setPageCurrent(1);
          setPageHistory(1);
        }}
        clientOptions={clientOptions}
        fromDate={activeTab === "current" ? currentFrom : historyFrom}
        setFromDate={activeTab === "current" ? setCurrentFrom : setHistoryFrom}
        toDate={activeTab === "current" ? currentTo : historyTo}
        setToDate={activeTab === "current" ? setCurrentTo : setHistoryTo}
        search={search}
        setSearch={(v) => {
          setSearch(v);
          setPageCurrent(1);
          setPageHistory(1);
        }}
        onRefresh={refreshActive}
      />
      <div className="h-[50vh] overflow-scroll">
        {/* Table + Pagination */}
        <SectionCard
          title={activeTab === "current" ? "Current Orders" : "Order History"}
          icon={<Clock size={16} />}
        >
          {activeTab === "current" ? (
            <>
              <OrdersTable
                rows={rowsCurrent}
                loading={loadingNew}
                onView={onView}
                onDelay={onDelayClick}
                page={pageCurrent}
                pageSize={PAGE_SIZE}
              />

              <Pagination
                page={pageCurrent}
                total={totalCurrent}
                pageSize={PAGE_SIZE}
                onChange={setPageCurrent}
              />
            </>
          ) : (
            <>
              <OrdersTable
                rows={rowsHistory}
                loading={loadingHis}
                onView={onView}
                page={pageHistory}
                pageSize={PAGE_SIZE}
              />
              <Pagination
                page={pageHistory}
                total={totalHistory}
                pageSize={PAGE_SIZE}
                onChange={setPageHistory}
              />
            </>
          )}
        </SectionCard>
      </div>

      {/* Details Modal */}
      <OrderDetailModal
        open={open}
        onClose={onCloseDetails}
        loading={detailLoading}
        order={selected}
        detail={detail}
        onAction={onAction}
        onStatusAction={handleStatusAction}
        isHistoryTab={activeTab === "history"}
      />

      {/* Cash Payment Modal (UNPAID) */}
      <DeliveryServiceType
        show={!!paymentOrder}
        onClose={handleClosePaymentModal}
        order={paymentOrder}
        amount={
          paymentOrder?.order_amount ||
          paymentOrder?.total_amount ||
          paymentOrder?.net_amount
        }
        onSuccess={handlePaymentSuccess}
      />

      {/* OrderPaidDetail Modal (PAID) */}
      <OrderPaidDetail
        show={!!paidOrder}
        onClose={() => setPaidOrder(null)}
        order={paidOrder}
        amount={
          paidOrder?.totalOrderPrice ||
          paidOrder?.pay_amount ||
          paidOrder?.order_amount ||
          paidOrder?.total_amount ||
          paidOrder?.net_amount
        }
        onSuccess={async () => {
          setPaidOrder(null);
          refreshActive();
        }}
      />
      <OrderEventsModal
        open={eventsOpen}
        onClose={() => {
          setEventsOpen(false);
          setEventsOrderId(null);
          setOrderEvents([]);
        }}
        loading={eventsLoading}
        orderId={eventsOrderId}
        events={orderEvents}
      />
    </div>
  );
}
